import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useGame } from "../context/GameContext";
import api from "../lib/api";
import { getSubject, ACHIEVEMENTS } from "../lib/content";
import { ASSETS } from "../lib/assets";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import {
  LogOut, ArrowLeft, Flame, BookOpen, Clock, TrendingUp, TrendingDown,
  UserPlus, Plus, Trash2, Share2,
} from "lucide-react";
import { Switch } from "../components/ui/switch";

const DAYS_NL = ["zo", "ma", "di", "wo", "do", "vr", "za"];
const emptyRow = () => ({ name: "", age: 6 });

const Card = ({ icon: Icon, label, value, color }) => (
  <div className="flex items-center gap-3 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-100">
    <div className="flex h-12 w-12 items-center justify-center rounded-xl" style={{ background: `${color}22`, color }}>
      <Icon className="h-6 w-6" />
    </div>
    <div>
      <p className="text-2xl font-bold text-slate-800">{value}</p>
      <p className="text-xs font-semibold text-slate-400">{label}</p>
    </div>
  </div>
);

// Small shareable progress card for a child.
function ShareCard({ stats }) {
  const p = stats.profile;
  const topBadges = (stats.achievements || []).slice(0, 3);
  const share = async () => {
    const text = `🦉 ${p.name} leert met SpeelSlim!\n🔥 ${stats.streak} dagen op rij · 📚 ${stats.total_lessons} lessen · ⏱️ ${stats.total_minutes} min\n🪙 ${p.coins} munten · ⭐ ${p.stars} sterren\n"Leren was nog nooit zo leuk!"`;
    try {
      if (navigator.share) await navigator.share({ title: "SpeelSlim voortgang", text });
      else { await navigator.clipboard.writeText(text); toast.success("Voortgang gekopieerd naar klembord!"); }
    } catch (e) { /* user cancelled */ }
  };
  return (
    <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-100">
      <h2 className="mb-3 font-display text-lg font-bold text-slate-800">Deelbaar voortgangskaartje</h2>
      <div data-testid="share-card" className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-sky-400 to-sky-500 p-5 text-white shadow-lg">
        <div className="flex items-center gap-4">
          <img src={ASSETS.lunaCelebrate} alt="Luna" className="h-20 w-20 shrink-0 object-contain drop-shadow" />
          <div>
            <p className="font-display text-2xl font-bold">{p.name}</p>
            <p className="text-sm font-bold text-white/80">{p.age} jaar · SpeelSlim</p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2 text-center">
          {[
            { l: "Reeks", v: `${stats.streak}🔥` },
            { l: "Lessen", v: stats.total_lessons },
            { l: "Munten", v: p.coins },
          ].map((s) => (
            <div key={s.l} className="rounded-2xl bg-white/20 py-2">
              <p className="font-display text-xl font-bold">{s.v}</p>
              <p className="text-[11px] font-bold text-white/80">{s.l}</p>
            </div>
          ))}
        </div>
        {topBadges.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {topBadges.map((b) => (
              <span key={b} className="rounded-full bg-white/20 px-2.5 py-1 text-xs font-bold">
                {ACHIEVEMENTS[b]?.icon} {ACHIEVEMENTS[b]?.title}
              </span>
            ))}
          </div>
        )}
        <p className="mt-3 font-display text-sm font-bold text-white/90">Leren was nog nooit zo leuk! 🎉</p>
      </div>
      <button data-testid="share-btn" onClick={share}
        className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 py-2.5 font-bold text-white shadow hover:bg-emerald-600">
        <Share2 className="h-4 w-4" /> Deel voortgang
      </button>
    </div>
  );
}

export default function ParentDashboard() {
  const { user, logout } = useAuth();
  const { clearActive } = useGame();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [stats, setStats] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [rows, setRows] = useState([emptyRow()]);
  const [addError, setAddError] = useState("");

  const loadProfiles = () =>
    api.get("/profiles").then(({ data }) => {
      setProfiles(data);
      return data;
    }).catch(() => []);

  useEffect(() => { if (user === false) navigate("/ouders"); }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    loadProfiles().then((data) => { if (data.length) setSelected(data[0].id); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!selected) { setStats(null); return; }
    api.get(`/profiles/${selected}/stats`).then(({ data }) => setStats(data)).catch(() => {});
  }, [selected]);

  const updateRow = (i, patch) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  const addRow = () => setRows((r) => [...r, emptyRow()]);
  const removeRow = (i) => setRows((r) => (r.length > 1 ? r.filter((_, idx) => idx !== i) : r));

  const createChildren = async () => {
    setAddError("");
    const valid = rows.filter((r) => r.name.trim());
    if (!valid.length) { setAddError("Vul minstens één naam in"); return; }
    try {
      const { data } = await api.post("/parent/children/bulk", {
        children: valid.map((r) => ({ name: r.name.trim() || "Kind", age: Number(r.age) })),
      });
      if (data.created.length) toast.success(`${data.created.length} kind(eren) toegevoegd!`);
      if (data.errors.length) {
        setAddError(data.errors.map((e) => `${e.name}: ${e.error}`).join(" · "));
      } else {
        setShowAdd(false); setRows([emptyRow()]);
      }
      const all = await loadProfiles();
      if (data.created.length) setSelected(data.created[data.created.length - 1].id);
      else if (all.length && !selected) setSelected(all[0].id);
    } catch (e) {
      setAddError(e.response?.data?.detail || "Er ging iets mis");
    }
  };

  if (!user) return <div className="min-h-screen flex items-center justify-center font-bold text-slate-400">Laden...</div>;

  const weekly = (stats?.weekly || []).map((d) => ({ ...d, day: DAYS_NL[new Date(d.date).getDay()] }));
  const subjectData = (stats?.by_subject || []).map((s) => ({
    name: getSubject(s.subject)?.title || s.subject,
    accuracy: s.accuracy, color: getSubject(s.subject)?.color || "#38BDF8",
  }));

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-slate-100 bg-white/90 px-4 py-3 backdrop-blur">
        <div className="flex items-center gap-3">
          <button data-testid="dashboard-back-btn" onClick={() => navigate("/")} className="rounded-full p-2 text-slate-500 hover:bg-slate-100">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="font-display text-xl font-bold text-slate-800">Ouder Dashboard</h1>
        </div>
        <button data-testid="dashboard-logout-btn" onClick={() => { clearActive(); logout(); navigate("/ouders"); }}
                className="flex items-center gap-2 rounded-full bg-slate-100 px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-200">
          <LogOut className="h-4 w-4" /> Uitloggen
        </button>
      </div>

      <div className="mx-auto max-w-5xl px-4 py-6">
        {/* profile selector + add child */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {profiles.map((p) => (
            <button key={p.id} data-testid={`profile-tab-${p.id}`} onClick={() => setSelected(p.id)}
              className={`rounded-full px-4 py-2 font-bold shadow-sm transition ${selected === p.id ? "bg-sky-500 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`}>
              {p.name} · {p.age}j
            </button>
          ))}
          <button data-testid="add-child-btn" onClick={() => { setShowAdd((v) => !v); setAddError(""); }}
            className="flex items-center gap-1.5 rounded-full bg-emerald-500 px-4 py-2 font-bold text-white shadow-sm hover:bg-emerald-600">
            <UserPlus className="h-4 w-4" /> Kinderen toevoegen
          </button>
        </div>

        {showAdd && (
          <div className="mb-6 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-100">
            <h3 className="mb-1 font-display text-lg font-bold text-slate-800">Kinderen toevoegen</h3>
            <p className="mb-4 text-sm text-slate-400">Voeg één of meerdere kinderen tegelijk toe. Kinderen hoeven niet in te loggen — jij kiest wie er speelt.</p>
            <div className="space-y-3">
              {rows.map((row, i) => (
                <div key={i} data-testid={`child-row-${i}`} className="grid gap-2 sm:grid-cols-[1.6fr,1fr,auto] sm:items-center">
                  <input data-testid={`child-name-${i}`} placeholder="Naam" value={row.name}
                    onChange={(e) => updateRow(i, { name: e.target.value })}
                    className="rounded-xl border-2 border-slate-200 px-3 py-2.5 font-semibold outline-none focus:border-sky-400" />
                  <select data-testid={`child-age-${i}`} value={row.age} onChange={(e) => updateRow(i, { age: e.target.value })}
                    className="rounded-xl border-2 border-slate-200 px-3 py-2.5 font-semibold outline-none focus:border-sky-400">
                    {[3,4,5,6,7,8,9,10,11,12].map((a) => <option key={a} value={a}>{a} jaar</option>)}
                  </select>
                  <button data-testid={`child-remove-${i}`} onClick={() => removeRow(i)}
                    className="flex items-center justify-center rounded-xl p-2.5 text-slate-400 hover:bg-red-50 hover:text-red-500">
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>
            {addError && <p data-testid="add-child-error" className="mt-2 text-sm font-bold text-red-500">{addError}</p>}
            <div className="mt-4 flex flex-wrap gap-2">
              <button data-testid="add-row-btn" onClick={addRow}
                className="flex items-center gap-1.5 rounded-xl bg-slate-100 px-4 py-2.5 font-bold text-slate-600 hover:bg-slate-200">
                <Plus className="h-4 w-4" /> Nog een kind
              </button>
              <button data-testid="add-child-submit" onClick={createChildren}
                className="rounded-xl bg-sky-500 px-5 py-2.5 font-bold text-white shadow hover:bg-sky-600">
                Accounts aanmaken
              </button>
            </div>
          </div>
        )}

        {profiles.length === 0 && !showAdd && (
          <div className="rounded-2xl bg-white p-8 text-center shadow-sm ring-1 ring-slate-100">
            <img src={ASSETS.lunaIdle} alt="Luna" className="mx-auto h-24 w-24 object-contain" />
            <p className="mt-2 font-display text-xl font-bold text-slate-700">Welkom bij SpeelSlim!</p>
            <p className="mt-1 text-slate-400">Maak accounts aan voor je kinderen om te beginnen.</p>
            <button data-testid="empty-add-child-btn" onClick={() => setShowAdd(true)}
              className="mt-4 inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-2.5 font-bold text-white shadow hover:bg-emerald-600">
              <UserPlus className="h-4 w-4" /> Eerste kind toevoegen
            </button>
          </div>
        )}

        {stats && (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Card icon={BookOpen} label="Lessen voltooid" value={stats.total_lessons} color="#38BDF8" />
              <Card icon={Clock} label="Leertijd (min)" value={stats.total_minutes} color="#4ADE80" />
              <Card icon={Flame} label="Dagen reeks" value={stats.streak} color="#EF4444" />
              <Card icon={TrendingUp} label="Sterk vak" value={getSubject(stats.strong)?.title || "-"} color="#A78BFA" />
            </div>

            <div className="mt-6 grid gap-4 lg:grid-cols-2">
              <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-100">
                <h2 className="mb-4 font-display text-lg font-bold text-slate-800">Activiteit deze week</h2>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={weekly}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                    <XAxis dataKey="day" tickLine={false} axisLine={false} fontSize={12} />
                    <YAxis allowDecimals={false} tickLine={false} axisLine={false} fontSize={12} />
                    <Tooltip />
                    <Bar dataKey="lessons" name="Lessen" fill="#38BDF8" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <ShareCard stats={stats} />
            </div>

            <div className="mt-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-100">
              <h2 className="mb-4 font-display text-lg font-bold text-slate-800">Nauwkeurigheid per vak</h2>
              {subjectData.length ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={subjectData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F1F5F9" />
                    <XAxis type="number" domain={[0, 100]} tickLine={false} axisLine={false} fontSize={12} />
                    <YAxis type="category" dataKey="name" width={90} tickLine={false} axisLine={false} fontSize={12} />
                    <Tooltip />
                    <Bar dataKey="accuracy" name="% goed" radius={[0, 8, 8, 0]}>
                      {subjectData.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="py-16 text-center text-slate-400">Nog geen lessen gespeeld.</p>}
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="flex items-center gap-3 rounded-2xl bg-emerald-50 p-4 ring-1 ring-emerald-100">
                <TrendingUp className="h-6 w-6 text-emerald-500" />
                <span className="font-bold text-slate-700">Sterk in: {getSubject(stats.strong)?.title || "nog onbekend"}</span>
              </div>
              <div className="flex items-center gap-3 rounded-2xl bg-orange-50 p-4 ring-1 ring-orange-100">
                <TrendingDown className="h-6 w-6 text-orange-500" />
                <span className="font-bold text-slate-700">Oefenen: {getSubject(stats.weak)?.title || "nog onbekend"}</span>
              </div>
            </div>

            <div className="mt-6 grid gap-4 lg:grid-cols-2">
              <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-100">
                <h2 className="mb-3 font-display text-lg font-bold text-slate-800">Vakken aan/uit</h2>
                <div className="space-y-2">
                  {Object.entries(stats.profile.enabled_subjects || {}).map(([sid, on]) => (
                    <div key={sid} className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-2.5">
                      <span className="font-bold capitalize text-slate-700">{getSubject(sid)?.title || sid}</span>
                      <Switch data-testid={`subject-toggle-${sid}`} checked={on}
                        onCheckedChange={async (v) => {
                          const subs = { ...stats.profile.enabled_subjects, [sid]: v };
                          const { data } = await api.patch(`/profiles/${selected}/subjects`, { subjects: subs });
                          setStats((s) => ({ ...s, profile: data }));
                        }} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-100">
                <h2 className="mb-3 font-display text-lg font-bold text-slate-800">Schermtijdlimiet</h2>
                <div className="flex flex-wrap gap-2">
                  {[0, 15, 30, 45, 60].map((m) => (
                    <button key={m} data-testid={`screentime-${m}`}
                      onClick={async () => {
                        const { data } = await api.patch(`/profiles/${selected}/screen-time`, { minutes: m });
                        setStats((s) => ({ ...s, profile: data }));
                      }}
                      className={`rounded-xl px-4 py-2 font-bold ${stats.profile.screen_time_minutes === m ? "bg-sky-500 text-white" : "bg-slate-100 text-slate-600"}`}>
                      {m === 0 ? "Geen" : `${m} min`}
                    </button>
                  ))}
                </div>
                <h2 className="mb-2 mt-5 font-display text-lg font-bold text-slate-800">Prestaties</h2>
                <div className="flex flex-wrap gap-2">
                  {(stats.achievements || []).length
                    ? stats.achievements.map((a) => (
                        <span key={a} className="rounded-full bg-yellow-50 px-3 py-1.5 text-sm font-bold text-yellow-700 ring-1 ring-yellow-100">
                          {ACHIEVEMENTS[a]?.icon} {ACHIEVEMENTS[a]?.title || a}
                        </span>
                      ))
                    : <span className="text-sm text-slate-400">Nog geen prestaties.</span>}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
